/*
 * Slides floating edge widgets (back to top, trust seals, chat bubbles) out of
 * the viewport while the visitor scrolls or drags, then brings them back once
 * activity stops. Mobile and tablet only.
 */
(function () {
  'use strict';

  var BREAKPOINT = '(max-width: 1023.98px)';
  var TUCK_ATTR = 'data-wls-tuck';
  var TUCKED_CLASS = 'wls-is-tucked';
  var TRANSITION = 'all .3s ease';
  var SIDES = ['left', 'right', 'top', 'bottom'];
  var DEFAULT_IDLE_MS = 800;
  var RESCAN_DEBOUNCE_MS = 300;

  var config = window.wlsFloatingTuck || {};
  var idleMs = toNumber(config.idleDelay, DEFAULT_IDLE_MS);
  var peek = Math.max(0, toNumber(config.peek, 0));
  var rules = parseRules(config.selectors);

  if (!rules.length || !window.matchMedia) return;

  var mediaQuery = window.matchMedia(BREAKPOINT);
  var entries = [];
  var idleTimer = null;
  var rescanTimer = null;
  var holdOpen = false;
  var holdTucked = false;

  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  function isUsableSelector(selector) {
    try {
      document.querySelector(selector);
      return true;
    } catch (error) {
      return false;
    }
  }

  function parseRules(raw) {
    if (typeof raw !== 'string' || !raw.trim()) return [];

    var seen = {};
    var parsed = [];

    raw.split(/[;\n]+/).forEach(function (line) {
      var selector = line.trim();
      if (!selector) return;

      var side = 'right';
      var match = selector.match(/^([\s\S]+?)\s+([a-z]+)$/i);

      if (match && SIDES.indexOf(match[2].toLowerCase()) !== -1) {
        selector = match[1].trim();
        side = match[2].toLowerCase();
      }

      var key = side + '|' + selector;
      if (!selector || seen[key] || !isUsableSelector(selector)) return;

      seen[key] = true;
      parsed.push({ selector: selector, side: side });
    });

    return parsed;
  }

  function applyTransition(el) {
    el.style.transition = TRANSITION;
  }

  // Everything is written inline so it wins over the widget's own stylesheet
  // without !important, and so its original inline values can be restored.
  // The transition is reasserted on every change because these widgets set
  // their own inline transition when they initialise, which can land after
  // collect() has already run.
  function setTucked(entry, tucked) {
    if (entry.tucked === tucked) return;

    var style = entry.el.style;
    entry.tucked = tucked;
    applyTransition(entry.el);

    if (tucked) {
      style.setProperty('translate', entry.x + 'px ' + entry.y + 'px');
      style.setProperty('pointer-events', 'none');
      entry.el.classList.add(TUCKED_CLASS);
      return;
    }

    restore(style, 'translate', entry.originalTranslate);
    restore(style, 'pointer-events', entry.originalPointerEvents);
    entry.el.classList.remove(TUCKED_CLASS);
  }

  function restore(style, property, original) {
    if (original) {
      style.setProperty(property, original);
    } else {
      style.removeProperty(property);
    }
  }

  function collect() {
    rules.forEach(function (rule) {
      var found;

      try {
        found = document.querySelectorAll(rule.selector);
      } catch (error) {
        return;
      }

      Array.prototype.forEach.call(found, function (el) {
        if (el.hasAttribute(TUCK_ATTR)) return;

        el.setAttribute(TUCK_ATTR, rule.side);
        applyTransition(el);

        entries.push({
          el: el,
          side: rule.side,
          tucked: false,
          x: 0,
          y: 0,
          originalTranslate: el.style.getPropertyValue('translate'),
          originalPointerEvents: el.style.getPropertyValue('pointer-events')
        });
      });
    });
  }

  function appliedTranslate(el) {
    var value = window.getComputedStyle(el).translate;

    if (!value || value === 'none' || value.indexOf('%') !== -1) {
      return { x: 0, y: 0 };
    }

    var parts = value.split(' ');
    var x = parseFloat(parts[0]);
    var y = parseFloat(parts[1]);

    return { x: isNaN(x) ? 0 : x, y: isNaN(y) ? 0 : y };
  }

  // Measured per tuck rather than cached, because these widgets animate
  // themselves into place and a value captured at load would freeze whatever
  // mid-flight position they happened to be in. Any translate we are still
  // animating is discounted so the offset is always relative to rest.
  function measure(entry) {
    var rect = entry.el.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    var applied = appliedTranslate(entry.el);
    var side = entry.side;
    var vertical = side === 'top' || side === 'bottom';
    var gap;

    if (side === 'left') {
      gap = rect.right - applied.x;
    } else if (side === 'right') {
      gap = window.innerWidth - rect.left + applied.x;
    } else if (side === 'top') {
      gap = rect.bottom - applied.y;
    } else {
      gap = window.innerHeight - rect.top + applied.y;
    }

    // Never travel less than the widget's own size: while it animates itself
    // into place the rect understates how far it still has to go.
    var size = vertical ? rect.height : rect.width;
    var distance = Math.max(0, Math.max(gap, size) - peek);

    if (!distance) return false;

    var offset = Math.round(side === 'left' || side === 'top' ? -distance : distance);

    entry.x = vertical ? 0 : offset;
    entry.y = vertical ? offset : 0;

    return true;
  }

  function tuck() {
    if (holdOpen || !mediaQuery.matches) return;

    entries.forEach(function (entry) {
      if (entry.tucked || !measure(entry)) return;
      setTucked(entry, true);
    });
  }

  function untuck() {
    entries.forEach(function (entry) {
      setTucked(entry, false);
    });
  }

  function scheduleUntuck() {
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(untuck, idleMs);
  }

  // Called straight from the scroll handler rather than via rAF: setTucked is a
  // no-op once the state matches, and a paused rAF (background tab) could
  // otherwise fire late and leave a widget stuck off-screen.
  function onActivity() {
    if (!mediaQuery.matches) return;

    tuck();

    // A finger resting on the screen still counts as interacting, so the idle
    // countdown only starts once it lifts.
    if (holdTucked) {
      window.clearTimeout(idleTimer);
      return;
    }

    scheduleUntuck();
  }

  // A touch on one of the tracked widgets keeps it out so it stays pressable;
  // a touch anywhere else counts as interacting with the page, which covers
  // presses that never turn into a scroll, such as slider arrows.
  function onPointerDown(event) {
    var target = event.target;

    if (target && target.closest && target.closest('[' + TUCK_ATTR + ']')) {
      holdOpen = true;
      window.clearTimeout(idleTimer);
      untuck();
      return;
    }

    holdTucked = true;
    onActivity();
  }

  function onPointerRelease() {
    holdOpen = false;

    if (!holdTucked) return;

    holdTucked = false;

    if (mediaQuery.matches) scheduleUntuck();
  }

  function rescan() {
    window.clearTimeout(rescanTimer);
    rescanTimer = window.setTimeout(collect, RESCAN_DEBOUNCE_MS);
  }

  function onViewportChange() {
    window.clearTimeout(idleTimer);
    untuck();
  }

  function onBreakpointChange() {
    if (mediaQuery.matches) {
      collect();
    } else {
      window.clearTimeout(idleTimer);
      untuck();
    }
  }

  function init() {
    collect();

    window.addEventListener('scroll', onActivity, { passive: true });
    window.addEventListener('touchmove', onActivity, { passive: true });
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);

    if (window.PointerEvent) {
      document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
      document.addEventListener('pointerup', onPointerRelease, { capture: true, passive: true });
      document.addEventListener('pointercancel', onPointerRelease, { capture: true, passive: true });
    } else {
      document.addEventListener('touchstart', onPointerDown, { capture: true, passive: true });
      document.addEventListener('touchend', onPointerRelease, { capture: true, passive: true });
      document.addEventListener('touchcancel', onPointerRelease, { capture: true, passive: true });
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', onBreakpointChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(onBreakpointChange);
    }

    document.addEventListener('shopify:section:load', rescan);

    if (window.MutationObserver && document.body) {
      new MutationObserver(rescan).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
